import { Injectable, inject } from '@angular/core';

import { type SuiSimpleChatFeedback, type SuiSimpleChatMessage } from '@semeris/ui';
import { EMPTY, Observable, defer } from 'rxjs';
import { map } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { AiChatStateService } from './ai-chat-state.service';
import { CommentService } from '../../comment/services/comment.service';
import { HelpAiFeedbackService } from '../../comment/services/help-ai-feedback.service';
import { HelpAiTransport } from '../../comment/services/help-ai-transport.service';

@Injectable({ providedIn: 'root' })
export class AiChatService {
  private readonly commentService = inject(CommentService);
  private readonly transport = inject(HelpAiTransport);
  private readonly feedback = inject(HelpAiFeedbackService);
  private readonly state = inject(AiChatStateService);

  /**
   * Send a user message and emit the assistant's HTML response. The conversation is reused across
   * turns, so the backend keeps the thread; the `history` the chat component passes is unused, but
   * the signature matches `SuiSimpleChatCallback`.
   */
  public ask(message: string): Observable<string> {
    return defer(() => {
      const conversationId = this.state.activeConversationId() ?? uuid();
      this.state.setActiveConversation(conversationId);

      return this.transport.ask(conversationId, message).pipe(map((html) => html || '<p>(No response received.)</p>'));
    });
  }

  /** Store a thumbs rating against the running conversation. Same labels the Agents page reviews. */
  public rate(rating: SuiSimpleChatFeedback): Observable<boolean> {
    const conversationId = this.state.activeConversationId();

    return conversationId ? this.feedback.rate(conversationId, rating) : EMPTY;
  }

  /** Load a conversation's prior turns as chat messages, for seeding the page when resuming. */
  public getHistory(conversationId: string): Observable<SuiSimpleChatMessage[]> {
    return this.commentService.getCommentsByConversation(conversationId).pipe(
      map((comments) =>
        comments.map(
          (c): SuiSimpleChatMessage => ({
            role: c.sentByMe ? 'user' : 'assistant',
            content: (c.sentByMe ? c.markdown : c.html) ?? c.html ?? '',
          }),
        ),
      ),
    );
  }

  public newConversation(): void {
    this.state.newConversation();
  }
}
